import {
	ContactSources,
	EmailEngagementSources,
	FormEngagementSources,
	AppTypes,
	Apps,
	ConnectorLiteApps
} from 'utils/constants';

const getDisabledContactSources = (rootGetters, apps) => {
	if (rootGetters['user/isCrmToCrmSyncEnabled']) {
		return [];
	}

	if (rootGetters['user/isHubspotCrmConnected']) {
		return apps
			.filter(app => app.type === AppTypes.CRM && app.id !== ContactSources.HUBSPOT);
	}

	if (rootGetters['user/isPipedriveConnected']) {
		return apps
			.filter(app => app.type === AppTypes.CRM && app.id !== ContactSources.PIPEDRIVE);
	}

	if (rootGetters['user/isCopperConnected']) {
		return apps
			.filter(app => app.type === AppTypes.CRM && app.id !== ContactSources.COPPER);
	}

	if (rootGetters['user/isAirtableConnected']) {
		return apps
			.filter(app => app.type === AppTypes.CRM && app.id !== ContactSources.AIRTABLE);
	}

	if (rootGetters['user/isSalesforceConnected']) {
		return apps
			.filter(app => app.type === AppTypes.CRM && app.id !== ContactSources.SALESFORCE);
	}

	if (rootGetters['user/isMondayConnected']) {
		return apps
			.filter(app => app.type === AppTypes.CRM && app.id !== ContactSources.MONDAY);
	}

	return [];
};

const getDisabledHubspotSources = (
	apps,
	connectionContactSource,
	connectionEngagementSource
) => {
	if (connectionContactSource === ContactSources.HUBSPOT) {
		return apps
			.filter(app => app.id === EmailEngagementSources.HUBSPOT_MARKETING);
	}

	if (connectionEngagementSource === EmailEngagementSources.HUBSPOT_MARKETING) {
		return apps
			.filter(app => app.id === ContactSources.HUBSPOT);
	}

	return [];
};

const getAvailableApps = (rootGetters, state) => state.apps
	.filter(({ id }) =>
		![state.connectionContactSource, state.connectionEngagementSource].includes(id))
	.filter(({ id }) =>
		!(id === ContactSources.AIRTABLE && !rootGetters['user/isConnectorEnabledForAirtable']))
	.filter(({ id }) =>
		!(id === FormEngagementSources.LINKEDIN && !rootGetters['user/isConnectorEnabledForLinkedin']))
	.filter(({ id }) =>
		!(id === FormEngagementSources.WIX && !rootGetters['user/isConnectorEnabledForWix']))
	.filter(({ id }) =>
		!(id === EmailEngagementSources.KLAVIYO && !rootGetters['user/isConnectorEnabledForKlaviyo']));

const isConnectorLiteApp = (rootGetters, app) => {
	const isConnectorLiteEnabled = rootGetters['user/isConnectorLiteEnabled'];

	if (!isConnectorLiteEnabled) {
		return true;
	}

	return ConnectorLiteApps.includes(app.id);
};

const removeUnavailableApps = (apps, state, rootGetters) =>
	apps.filter(app => isConnectorLiteApp(rootGetters, app))
		.filter(({ id }) => !getDisabledContactSources(rootGetters, state.apps)
			.some(disabledContactSource => disabledContactSource.id === id))
		.filter(({ id }) => !getDisabledHubspotSources(
			state.apps, state.connectionContactSource, state.connectionEngagementSource
		).some(disabledContactSource => disabledContactSource.id === id));

export const getters = {
	availableApps(state, localGetters, rootState, rootGetters) {
		return removeUnavailableApps(getAvailableApps(rootGetters, state), state, rootGetters);
	},

	integrations(state, localGetters, rootState, rootGetters) {
		return removeUnavailableApps(state.apps, state, rootGetters);
	},

	disabledHubspotSources(state, localGetters, rootState, rootGetters) {
		return getAvailableApps(rootGetters, state)
			.filter(app => isConnectorLiteApp(rootGetters, app))
			.filter(({ id }) => getDisabledHubspotSources(
				state.apps,
				state.connectionContactSource,
				state.connectionEngagementSource
			).some(disabledContactSource => disabledContactSource.id === id))
			.filter(({ id }) => !getDisabledContactSources(rootGetters, state.apps)
				.some(disabledContactSource => disabledContactSource.id === id));
	},

	appAuthData(state) {
		if (state.appAuthAppId === Apps.COPPER) {
			return {
				email: state.appAuthAccountId,
				apiKey: state.appAuthApiKey
			};
		}

		if (state.appAuthAppId === Apps.ACTIVECAMPAIGN) {
			return {
				accountName: state.appAuthAccountId,
				apiKey: state.appAuthApiKey
			};
		}

		return {};
	},

	isEngagementSyncSupported(state) {
		const blockedEngagementSources = [
			EmailEngagementSources.MAILERLITE,
			EmailEngagementSources.GETRESPONSE,
			EmailEngagementSources.SENDGRID,
			EmailEngagementSources.CONSTANT_CONTACT,
			EmailEngagementSources.GOOGLE_SHEETS
		];

		const isValidEngagementSource = Object.values(EmailEngagementSources)
			.includes(state.connectionEngagementSource)
			&& !blockedEngagementSources.includes(state.connectionEngagementSource);

		const contactSourceApp = state.apps.find(app => app.id === state.connectionContactSource);
		const isContactSourceCrm = contactSourceApp?.type === AppTypes.CRM;

		return isValidEngagementSource && isContactSourceCrm;
	}
};
